import React, { useState, useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2,
  X,
  Loader2,
  AlertCircle,
  CheckCircle2,
  User,
  Phone,
  MapPin,
  FileText,
  AlertTriangle,
  Building,
  Plus,
  Trash2,
  Mail,
  Briefcase
} from "lucide-react";
import { Customer, CreateCustomerData } from "../../../services/customerService";
import Select from "../../../shared/components/form/Select";
import Button from "../../../shared/components/ui/button/Button";

const BRAND_COLORS = {
  primary: "#339274",          // Primary Green
  primaryHover: "#29795f",     // Darker Green for hover
  primaryLight: "#F8FCFA",     // Very subtle green background
  primarySoft: "#EDF7F2",      // Soft green background
  accent: "#A3CD39",           // Lime accent
  dark: "#007F66",             // Deep teal
  success: "#10B981",
  successLight: "#F0FDF4",
  error: "#EF4444",
  errorLight: "#FEF2F2",
  warning: "#F59E0B",
  warningLight: "#FFFBEB",
};

interface Props {
  onAdd: (customerData: CreateCustomerData) => Promise<void>;
  onClose?: () => void;
  editingCustomer?: Customer | null;
  showSuccessAndClose?: boolean; // Controls whether form shows success message and auto-closes
}

const schema = yup.object({
  companyName: yup.string()
    .required("Company name is required")
    .min(2, "Company name must be at least 2 characters")
    .max(200, "Company name must be less than 200 characters"),
  code: yup.string()
    .required("Company code is required")
    .min(3, "Code must be at least 3 characters")
    .max(20, "Code must be less than 20 characters")
    .matches(/^[A-Z0-9-]+$/, "Code must contain only uppercase letters, numbers, and hyphens"),
  // customerName is derived from first contact's name, not a separate field
  customerName: yup.string().optional(),
  customerType: yup.string()
    .required("Company type is required")
    .oneOf(['LLP', 'Pvt Ltd', 'Proprietor', 'Public Ltd', 'Partnership', 'Sole Proprietorship', 'Other'], "Please select a valid company type"),
  email: yup.string()
    .required("Email is required")
    .email("Please enter a valid email address"),
  companyContact: yup.string()
    .required("Company contact is required")
    .matches(/^\+?[\d\s\-\(\)]{10,15}$/, "Please enter a valid contact number"),
  contacts: yup.array().of(
    yup.object({
      name: yup.string()
        .required("Contact person name is required")
        .min(2, "Name must be at least 2 characters")
        .test('not-empty', 'Contact person name is required', (value) => {
          return value !== undefined && value !== null && typeof value === 'string' && value.trim() !== '';
        }),
      mobile: yup.string()
        .required("Contact person phone number is required")
        .matches(/^\+?[\d\s\-\(\)]{10,15}$/, "Please enter a valid contact number")
        .test('not-empty', 'Contact person phone number is required', (value) => {
          return value !== undefined && value !== null && typeof value === 'string' && value.trim() !== '';
        }),
      email: yup.string()
        .required("Email is required")
        .email("Please enter a valid email address")
        .test('not-empty', 'Email is required', (value) => {
          return value !== undefined && value !== null && typeof value === 'string' && value.trim() !== '';
        }),
      designation: yup.string()
        .required("Designation is required")
        .min(2, "Designation must be at least 2 characters")
        .test('not-empty', 'Designation is required', (value) => {
          return value !== undefined && value !== null && typeof value === 'string' && value.trim() !== '';
        }),
      isPrimary: yup.boolean().default(false),
    })
  )
  .min(1, "At least one contact person is required")
  .test('at-least-one-complete', 'At least one contact must have all required fields filled', function(contacts) {
    if (!contacts || contacts.length === 0) {
      return this.createError({ message: 'At least one contact is required' });
    }
    
    // Check if at least one contact has all required fields
    const hasCompleteContact = contacts.some(contact => {
      return contact?.name?.trim() && 
             contact?.mobile?.trim() && 
             contact?.email?.trim() && 
             contact?.designation?.trim();
    });
    
    if (!hasCompleteContact) {
      return this.createError({ message: 'At least one contact must have all required fields (name, mobile, email, designation) filled' });
    }
    
    return true;
  }),
  // Address fields - Required
  street: yup.string()
    .required("Street address is required")
    .min(2, "Street must be at least 2 characters")
    .max(200, "Street must be less than 200 characters"),
  city: yup.string()
    .required("City is required")
    .min(2, "City must be at least 2 characters")
    .max(100, "City must be less than 100 characters"),
  state: yup.string()
    .required("State is required")
    .min(2, "State must be at least 2 characters")
    .max(100, "State must be less than 100 characters"),
  country: yup.string()
    .required("Country is required")
    .max(100, "Country must be less than 100 characters"),
  pincode: yup.string()
    .required("Pincode is required")
    .matches(/^\d{6}$/, "Pincode must be 6 digits"),
});

interface FormValues {
  companyName: string;
  code: string;
  customerName: string;
  customerType: string;
  email: string;
  companyContact: string;
  contacts: {
    name: string;
    mobile: string;
    email: string;
    designation: string;
    isPrimary: boolean;
  }[];
  street: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
}

export default function CustomerForm({ onAdd, onClose, editingCustomer, showSuccessAndClose = true }: Props) {
  const [apiError, setApiError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const isEditMode = Boolean(editingCustomer);

  const customerTypeOptions = [
    { value: 'LLP', label: 'LLP (Limited Liability Partnership)' },
    { value: 'Pvt Ltd', label: 'Private Limited' },
    { value: 'Proprietor', label: 'Proprietor' },
    { value: 'Public Ltd', label: 'Public Limited' },
    { value: 'Partnership', label: 'Partnership' },
    { value: 'Sole Proprietorship', label: 'Sole Proprietorship' },
    { value: 'Other', label: 'Other' }
  ];

  // Set default values based on edit mode
  const getDefaultValues = (): Partial<FormValues> => {
    if (editingCustomer) {
      return {
        companyName: editingCustomer.companyName || editingCustomer.name || '',
        code: editingCustomer.code,
        customerName: editingCustomer.name,
        customerType: editingCustomer.customerType,
        email: editingCustomer.email || '',
        companyContact: editingCustomer.phone || '',
        contacts: editingCustomer.contacts && editingCustomer.contacts.length > 0
          ? (() => {
              const mappedContacts = editingCustomer.contacts.map(c => ({ 
                name: c.name || '', 
                mobile: c.mobile || c.phone || '',
                email: c.email || '',
                designation: c.designation || '',
                isPrimary: c.isPrimary || false
              }));
              // Ensure at least one contact is primary
              const hasPrimary = mappedContacts.some(c => c.isPrimary);
              if (!hasPrimary && mappedContacts.length > 0) {
                mappedContacts[0].isPrimary = true;
              }
              return mappedContacts;
            })()
          : [{ 
              name: editingCustomer.name || '', 
              mobile: editingCustomer.mobile || '',
              email: editingCustomer.email || '',
              designation: '',
              isPrimary: true
            }],
        street: editingCustomer.address?.street || '',
        city: editingCustomer.address?.city || '',
        state: editingCustomer.address?.state || '',
        country: editingCustomer.address?.country || 'India',
        pincode: editingCustomer.address?.pincode || '',
      };
    }
    return {
      country: 'India',
      customerType: 'Pvt Ltd',
      contacts: [{ name: '', mobile: '', email: '', designation: '', isPrimary: true }]
    };
  };

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
    setValue,
    getValues
  } = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: getDefaultValues(),
    shouldUnregister: false
  });

  // Helper function to check if all required fields are filled
  const areAllRequiredFieldsFilled = (): boolean => {
    try {
      const values = getValues();
      
      // Check basic required fields (excluding customerName as it's derived from first contact)
      const basicFields = {
        companyName: values.companyName?.trim(),
        code: values.code?.trim(),
        customerType: values.customerType,
        email: values.email?.trim(),
        companyContact: values.companyContact?.trim(),
        street: values.street?.trim(),
        city: values.city?.trim(),
        state: values.state?.trim(),
        country: values.country?.trim(),
        pincode: values.pincode?.trim()
      };

      // Log which fields are missing
      const missingFields = Object.entries(basicFields)
        .filter(([, value]) => !value)
        .map(([key]) => key);
      
      if (missingFields.length > 0) {
        console.log('Missing basic fields:', missingFields);
        return false;
      }

      // Check contacts array - at least one contact is required
      if (!values.contacts || values.contacts.length === 0) {
        console.log('No contacts found - at least one contact is required');
        return false;
      }

      // Check that at least one contact has all required fields filled
      let hasAtLeastOneValidContact = false;
      
      for (let i = 0; i < values.contacts.length; i++) {
        const contact = values.contacts[i];
        if (!contact) continue;
        
        const contactFields = {
          name: contact?.name?.trim(),
          mobile: contact?.mobile?.trim(),
          email: contact?.email?.trim(),
          designation: contact?.designation?.trim()
        };
        
        // Check if this contact has all required fields
        const missingContactFields = Object.entries(contactFields)
          .filter(([, value]) => !value)
          .map(([key]) => key);
        
        if (missingContactFields.length === 0) {
          // This contact is fully filled
          hasAtLeastOneValidContact = true;
        } else {
          console.log(`Contact ${i} missing fields:`, missingContactFields);
        }
      }

      if (!hasAtLeastOneValidContact) {
        console.log('At least one contact with all required fields is needed');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking required fields:', error);
      return false;
    }
  };

  // Helper function to check if there are any validation errors (including nested)
  const hasValidationErrors = (): boolean => {
    if (Object.keys(errors).length === 0) {
      return false;
    }
    
    // Check for nested errors in contacts array
    if (errors.contacts) {
      for (let i = 0; i < (errors.contacts as any)?.length || 0; i++) {
        const contactErrors = (errors.contacts as any)?.[i];
        if (contactErrors && Object.keys(contactErrors).length > 0) {
          return true;
        }
      }
    }
    
    return Object.keys(errors).length > 0;
  };


  // Watch fields for success states and form validation
  const watchedCompanyName = watch("companyName");
  const watchedCode = watch("code");
  const watchedEmail = watch("email");
  const watchedCompanyContact = watch("companyContact");
  const watchedStreet = watch("street");
  const watchedCity = watch("city");
  const watchedState = watch("state");
  const watchedPincode = watch("pincode");
  
  // Compute if form is valid - this will re-compute when watched fields change
  const isFormValid = areAllRequiredFieldsFilled() && !hasValidationErrors();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "contacts"
  });

  // Reset form when editing customer changes
  useEffect(() => {
    reset(getDefaultValues());
  }, [editingCustomer, reset]);

  // Auto-generate customer code based on company name (only for new customers)
  useEffect(() => {
    if (!isEditMode && watchedCompanyName && !watch("code")) {
      // Generate code with CUST prefix and serial number
      const cleanName = watchedCompanyName
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .substring(0, 8);
      if (cleanName) {
        // For now, use a simple timestamp-based serial, in real app this would come from backend
        const serial = Date.now().toString().slice(-4);
        const generatedCode = `CUST${serial}`;
        setValue("code", generatedCode);
      }
    }
  }, [watchedCompanyName, setValue, watch, isEditMode]);


  const onSubmit = async (data: FormValues) => {
    setApiError(null);
    setShowSuccess(false);

    // Validate that all required fields are present
    if (!areAllRequiredFieldsFilled()) {
      console.log('Validation failed: Not all required fields are filled');
      toast.error('Please fill all required fields');
      return;
    }

    try {
      // Find the primary contact
      const primaryContactIndex = data.contacts.findIndex(c => c.isPrimary);
      const primaryContact = primaryContactIndex >= 0 ? data.contacts[primaryContactIndex] : data.contacts[0];
      
      // Ensure at least one contact is primary (if none is selected, make the first one primary)
      const contactsWithPrimary = data.contacts.map((c, index) => ({
        ...c,
        isPrimary: primaryContactIndex >= 0 ? c.isPrimary : index === 0
      }));

      const customerData: CreateCustomerData = {
        name: primaryContact.name.trim(), // Use primary contact as primary name
        code: data.code.trim().toUpperCase(),
        companyName: data.companyName.trim(),
        customerType: data.customerType as any,
        email: data.email.trim().toLowerCase(),
        phone: data.companyContact?.trim() || '',
        mobile: primaryContact.mobile?.trim() ?? '',
        address: {
          street: data.street.trim(),
          city: data.city.trim(),
          state: data.state.trim(),
          country: data.country || 'India',
          pincode: data.pincode.trim(),
        },
        contacts: contactsWithPrimary.map((c) => ({
          name: c.name?.trim() || null,
          phone: c.mobile?.trim() || null,
          mobile: c.mobile?.trim() || null,
          designation: c.designation?.trim() || null,
          email: c.email?.trim().toLowerCase() || null,
          isPrimary: c.isPrimary || false
        })),
        industry: '',
      };

      // Call the parent component's handler
      await onAdd(customerData);

      // Only show success message and auto-close if parent wants us to
      if (showSuccessAndClose) {
        // Show success message
        setShowSuccess(true);
        toast.success(
          isEditMode
            ? "Customer updated successfully!"
            : "Customer created successfully!"
        );

        // Close form after a short delay to show success message
        setTimeout(() => {
          onClose?.();
          reset();
        }, 1500);
      } else {
        // Parent is handling success/close, just reset the form
        reset();
      }
    } catch (err: any) {
      // Extract specific error message from the backend
      let message = "Failed to save customer. Please try again.";
      
      if (err.response?.data?.message) {
        message = err.response.data.message;
      } else if (err.message) {
        message = err.message;
      }

      // Cleanup Joi validation messages (e.g., remove escaped quotes around field names)
      if (typeof message === 'string') {
        message = message.replace(/"([^"]+)"/g, '$1'); // Change "email" to email
        
        // Handle specific common error cases
        if (message.toLowerCase().includes("duplicate key error")) {
          if (message.toLowerCase().includes("code")) {
            message = "Customer code already exists. Please use a different code.";
          } else if (message.toLowerCase().includes("email")) {
            message = "Email address already exists. Please use a different email.";
          }
        } else if (message.includes("Customer validation failed")) {
          message = "Please check all required fields and try again.";
        }
      }

      setApiError(message);
      console.error("Customer Save Error:", err);
      
      // Also show a toast for immediate feedback if the form is long
      // toast.error(message);
    }
  };

  const formTitle = isEditMode ? "Edit Customer" : "Add New Customer";


  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col"
    >
      {/* Success Message */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-lg border mb-4"
            style={{
              backgroundColor: BRAND_COLORS.successLight,
              borderColor: `${BRAND_COLORS.success}40`
            }}
          >
            <div className="w-10 h-10 rounded-lg flex items-center justify-center border"
              style={{
                backgroundColor: BRAND_COLORS.success + '20',
                borderColor: BRAND_COLORS.success + '40'
              }}
            >
              <CheckCircle2 className="w-5 h-5" style={{ color: BRAND_COLORS.success }} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold" style={{ color: BRAND_COLORS.success }}>
                Customer {isEditMode ? 'updated' : 'created'} successfully!
              </p>
              <p className="text-xs mt-1" style={{ color: BRAND_COLORS.success }}>
                {isEditMode ? 'Customer information has been updated.' : 'Customer has been added to the system.'}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Alert */}
      <AnimatePresence>
        {apiError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-lg border mb-4"
            style={{
              backgroundColor: BRAND_COLORS.errorLight,
              borderColor: `${BRAND_COLORS.error}40`
            }}
          >
            <div className="w-10 h-10 rounded-lg flex items-center justify-center border"
              style={{
                backgroundColor: BRAND_COLORS.error + '20',
                borderColor: BRAND_COLORS.error + '40'
              }}
            >
              <AlertTriangle className="w-5 h-5" style={{ color: BRAND_COLORS.error }} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold" style={{ color: BRAND_COLORS.error }}>Error</p>
              <p className="text-xs mt-1" style={{ color: BRAND_COLORS.error }}>{apiError}</p>
            </div>
            <button
              onClick={() => setApiError(null)}
              className="p-1.5 rounded-lg hover:bg-red-100 transition-colors"
              style={{ color: BRAND_COLORS.error }}
            >
              <X size={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Form Header */}
      <div className="flex items-center gap-3 pb-5 border-b border-gray-200 mb-6">
        <div className="w-12 h-12 rounded-lg flex items-center justify-center border"
          style={{
            background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
            borderColor: BRAND_COLORS.primary
          }}
        >
          <Building2 className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-gray-900">{formTitle}</h2>
          <p className="text-sm text-gray-600 mt-1 leading-relaxed">
            {isEditMode
              ? "Update customer information and contact details"
              : "Create a new customer with company and contact information"
            }
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X size={20} className="text-gray-500" />
        </button>
      </div>

      {/* Form Body - Scrollable */}
      <div className="flex-1 overflow-y-auto pr-2">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Basic Information */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-6 bg-blue-500 rounded-full"></div>
              <h3 className="text-base font-semibold text-gray-900">Basic Information</h3>
            </div>

            {/* Company Name */}
            <div className="space-y-2">
              <label htmlFor="companyName" className="block text-sm font-medium text-gray-900">
                Company Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building size={16} className="text-gray-500" />
                </div>
                <input
                  id="companyName"
                  type="text"
                  {...register("companyName")}
                  className={`h-10 w-full rounded-lg border bg-white pl-9 pr-10 py-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.companyName
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                    : watchedCompanyName && !errors.companyName
                      ? `focus:ring-opacity-20`
                      : "border-gray-300 hover:border-gray-400"
                    }`}
                  style={watchedCompanyName && !errors.companyName ? {
                    borderColor: BRAND_COLORS.primary,
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : !errors.companyName ? {
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : {}}
                  placeholder="Enter company name"
                />
                {watchedCompanyName && !errors.companyName && (
                  <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                    style={{ color: BRAND_COLORS.primary }} />
                )}
                {errors.companyName && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
                )}
              </div>
              {errors.companyName && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle className="w-3 h-3 flex-shrink-0" />
                  {errors.companyName.message}
                </p>
              )}
            </div>

            {/* Company Code */}
            <div className="space-y-2">
              <label htmlFor="code" className="block text-sm font-medium text-gray-900">
                Company Code <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FileText size={16} className="text-gray-500" />
                </div>
                <input
                  id="code"
                  type="text"
                  {...register("code", {
                    onChange: (e) => {
                      // Auto-convert to uppercase and remove invalid characters
                      const value = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
                      e.target.value = value;
                      return value;
                    }
                  })}
                  className={`h-10 w-full rounded-lg border bg-white pl-9 pr-10 py-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.code
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                    : watchedCode && !errors.code
                      ? `focus:ring-opacity-20`
                      : "border-gray-300 hover:border-gray-400"
                    }`}
                  style={watchedCode && !errors.code ? {
                    borderColor: BRAND_COLORS.primary,
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : !errors.code ? {
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : {}}
                  placeholder="e.g., CUST001"
                  disabled={isEditMode}
                />
                {watchedCode && !errors.code && (
                  <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                    style={{ color: BRAND_COLORS.primary }} />
                )}
                {errors.code && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
                )}
              </div>
              {errors.code && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle className="w-3 h-3 flex-shrink-0" />
                  {errors.code.message}
                </p>
              )}
              {isEditMode && (
                <p className="text-xs text-gray-500">Company code cannot be changed</p>
              )}
            </div>

            {/* Company Type */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-900">
                Company Type <span className="text-red-500">*</span>
              </label>
              <Controller
                name="customerType"
                control={control}
                render={({ field }) => (
                  <Select
                    options={customerTypeOptions}
                    value={field.value}
                    onChange={(option) => field.onChange(option?.value || "")}
                    placeholder="Select company type"
                    className="h-10"
                    error={errors.customerType?.message}
                  />
                )}
              />
              {errors.customerType && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle className="w-3 h-3 flex-shrink-0" />
                  {errors.customerType.message}
                </p>
              )}
            </div>

            {/* Company Email */}
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium text-gray-900">
                Company Email <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User size={16} className="text-gray-500" />
                </div>
                <input
                  id="email"
                  type="email"
                  {...register("email")}
                  className={`h-10 w-full rounded-lg border bg-white pl-9 pr-10 py-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.email
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                    : watchedEmail && !errors.email
                      ? `focus:ring-opacity-20`
                      : "border-gray-300 hover:border-gray-400"
                    }`}
                  style={watchedEmail && !errors.email ? {
                    borderColor: BRAND_COLORS.primary,
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : !errors.email ? {
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : {}}
                  placeholder="Enter company email address"
                />
                {watchedEmail && !errors.email && (
                  <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                    style={{ color: BRAND_COLORS.primary }} />
                )}
                {errors.email && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
                )}
              </div>
              {errors.email && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle className="w-3 h-3 flex-shrink-0" />
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Company Number */}
            <div className="space-y-2">
              <label htmlFor="companyContact" className="block text-sm font-medium text-gray-900">
                Company Number <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone size={16} className="text-gray-500" />
                </div>
                <input
                  id="companyContact"
                  type="tel"
                  {...register("companyContact")}
                  className={`h-10 w-full rounded-lg border bg-white pl-9 pr-10 py-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.companyContact
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                    : watchedCompanyContact && !errors.companyContact
                      ? `focus:ring-opacity-20`
                      : "border-gray-300 hover:border-gray-400"
                    }`}
                  style={watchedCompanyContact && !errors.companyContact ? {
                    borderColor: BRAND_COLORS.primary,
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : !errors.companyContact ? {
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : {}}
                  placeholder="Enter company contact number"
                />
                {watchedCompanyContact && !errors.companyContact && (
                  <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                    style={{ color: BRAND_COLORS.primary }} />
                )}
                {errors.companyContact && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
                )}
              </div>
              {errors.companyContact && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle className="w-3 h-3 flex-shrink-0" />
                  {errors.companyContact.message}
                </p>
              )}
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-6 bg-green-500 rounded-full"></div>
                <h3 className="text-base font-semibold text-gray-900">Contact Information</h3>
              </div>
              <button
                type="button"
                onClick={() => append({ name: '', mobile: '', email: '', designation: '', isPrimary: false })}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-lg transition-all"
                style={{ backgroundColor: BRAND_COLORS.primary }}
              >
                <Plus size={14} />
                Add Contact
              </button>
            </div>

            <div className="space-y-6">
              {fields.map((field, index) => {
                const isPrimary = watch(`contacts.${index}.isPrimary`);
                return (
                <div key={field.id} className={`space-y-4 p-4 rounded-xl border-2 transition-all duration-200 ${isPrimary ? 'bg-white border-green-100 shadow-sm' : 'bg-gray-100/50 border-gray-200 dashed'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-gray-700">Contact Person #{index + 1}</span>
                      {isPrimary && (
                        <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold">Primary</span>
                      )}
                      {!isPrimary && (
                        <span className="text-[10px] bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold">Secondary</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {/* Primary/Secondary Toggle */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-600 font-medium">Primary Contact</span>
                        <Controller
                          name={`contacts.${index}.isPrimary`}
                          control={control}
                          render={({ field: { value, onChange } }) => (
                            <button
                              type="button"
                              onClick={() => {
                                const currentContacts = getValues('contacts');
                                
                                // If setting this to primary, set all others to false
                                if (!value) {
                                  currentContacts.forEach((_, i) => {
                                    if (i !== index) {
                                      setValue(`contacts.${i}.isPrimary`, false);
                                    }
                                  });
                                  onChange(true);
                                } else {
                                  // If trying to unset primary, ensure at least one remains primary
                                  const otherPrimaryContacts = currentContacts.filter((_, i) => i !== index && currentContacts[i]?.isPrimary);
                                  
                                  // If this is the only primary contact and there are other contacts, make the first other contact primary
                                  if (otherPrimaryContacts.length === 0 && currentContacts.length > 1) {
                                    // Find the first non-primary contact and make it primary
                                    const firstNonPrimaryIndex = currentContacts.findIndex((_, i) => i !== index);
                                    if (firstNonPrimaryIndex >= 0) {
                                      setValue(`contacts.${firstNonPrimaryIndex}.isPrimary`, true);
                                    }
                                    onChange(false);
                                  } else if (currentContacts.length === 1) {
                                    // If this is the only contact, keep it as primary
                                    toast.warning('At least one contact must be primary');
                                    return;
                                  } else {
                                    onChange(false);
                                  }
                                }
                              }}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                                value 
                                  ? 'bg-green-500' 
                                  : 'bg-gray-300'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  value ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          )}
                        />
                      </div>
                      {index > 0 && (
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Contact Name */}
                    <div className="space-y-2">
                      <label htmlFor={`contacts.${index}.name`} className="block text-sm font-medium text-gray-900">
                        Contact Name *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <User size={16} className="text-gray-500" />
                        </div>
                        <input
                          id={`contacts.${index}.name`}
                          type="text"
                          {...register(`contacts.${index}.name` as const)}
                          className={`h-10 w-full rounded-lg border bg-white pl-9 pr-10 py-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.contacts?.[index]?.name
                            ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                            : "border-gray-300 hover:border-gray-400"
                            }`}
                          placeholder="Enter contact name"
                        />
                        {errors.contacts?.[index]?.name && (
                          <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
                        )}
                      </div>
                      {errors.contacts?.[index]?.name && (
                        <p className="flex items-center gap-1.5 text-xs text-red-600">
                          <AlertCircle className="w-3 h-3 flex-shrink-0" />
                          {errors.contacts?.[index]?.name?.message}
                        </p>
                      )}
                    </div>

                    {/* Contact Number */}
                    <div className="space-y-2">
                      <label htmlFor={`contacts.${index}.mobile`} className="block text-sm font-medium text-gray-900">
                        Contact Number *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Phone size={16} className="text-gray-500" />
                        </div>
                        <input
                          id={`contacts.${index}.mobile`}
                          type="tel"
                          {...register(`contacts.${index}.mobile` as const)}
                          onKeyPress={(e) => {
                            if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                              e.preventDefault();
                            }
                          }}
                          className={`h-10 w-full rounded-lg border bg-white pl-9 pr-10 py-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.contacts?.[index]?.mobile
                            ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                            : "border-gray-300 hover:border-gray-400"
                            }`}
                          placeholder="Enter contact number"
                        />
                        {errors.contacts?.[index]?.mobile && (
                          <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
                        )}
                      </div>
                      {errors.contacts?.[index]?.mobile && (
                        <p className="flex items-center gap-1.5 text-xs text-red-600">
                          <AlertCircle className="w-3 h-3 flex-shrink-0" />
                          {errors.contacts?.[index]?.mobile?.message}
                        </p>
                      )}
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <label htmlFor={`contacts.${index}.email`} className="block text-sm font-medium text-gray-900">
                        Email *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail size={16} className="text-gray-500" />
                        </div>
                        <input
                          id={`contacts.${index}.email`}
                          type="email"
                          {...register(`contacts.${index}.email` as const)}
                          className={`h-10 w-full rounded-lg border bg-white pl-9 pr-10 py-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.contacts?.[index]?.email
                            ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                            : "border-gray-300 hover:border-gray-400"
                            }`}
                          placeholder="Enter email address"
                        />
                        {errors.contacts?.[index]?.email && (
                          <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
                        )}
                      </div>
                      {errors.contacts?.[index]?.email && (
                        <p className="flex items-center gap-1.5 text-xs text-red-600">
                          <AlertCircle className="w-3 h-3 flex-shrink-0" />
                          {errors.contacts?.[index]?.email?.message}
                        </p>
                      )}
                    </div>

                    {/* Designation */}
                    <div className="space-y-2">
                      <label htmlFor={`contacts.${index}.designation`} className="block text-sm font-medium text-gray-900">
                        Designation *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Briefcase size={16} className="text-gray-500" />
                        </div>
                        <input
                          id={`contacts.${index}.designation`}
                          type="text"
                          {...register(`contacts.${index}.designation` as const)}
                          className={`h-10 w-full rounded-lg border bg-white pl-9 pr-10 py-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.contacts?.[index]?.designation
                            ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                            : "border-gray-300 hover:border-gray-400"
                            }`}
                          placeholder="Enter designation"
                        />
                        {errors.contacts?.[index]?.designation && (
                          <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
                        )}
                      </div>
                      {errors.contacts?.[index]?.designation && (
                        <p className="flex items-center gap-1.5 text-xs text-red-600">
                          <AlertCircle className="w-3 h-3 flex-shrink-0" />
                          {errors.contacts?.[index]?.designation?.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-6 bg-purple-500 rounded-full"></div>
              <h3 className="text-base font-semibold text-gray-900">Company Address</h3>
            </div>

            {/* Street Address */}
            <div className="space-y-2">
              <label htmlFor="street" className="block text-sm font-medium text-gray-900">
                Street Address <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MapPin size={16} className="text-gray-500" />
                </div>
                <input
                  id="street"
                  type="text"
                  {...register("street")}
                  className={`h-10 w-full rounded-lg border bg-white pl-9 pr-10 py-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.street
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                    : watchedStreet && !errors.street
                      ? `focus:ring-opacity-20`
                      : "border-gray-300 hover:border-gray-400"
                    }`}
                  style={watchedStreet && !errors.street ? {
                    borderColor: BRAND_COLORS.primary,
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : !errors.street ? {
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : {}}
                  placeholder="Enter street address"
                />
                {watchedStreet && !errors.street && (
                  <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                    style={{ color: BRAND_COLORS.primary }} />
                )}
                {errors.street && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
                )}
              </div>
              {errors.street && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle className="w-3 h-3 flex-shrink-0" />
                  {errors.street.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* City */}
              <div className="space-y-2">
                <label htmlFor="city" className="block text-sm font-medium text-gray-900">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  id="city"
                  type="text"
                  {...register("city")}
                  className={`h-10 w-full rounded-lg border bg-white px-3 py-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.city
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                    : watchedCity && !errors.city
                      ? `focus:ring-opacity-20`
                      : "border-gray-300 hover:border-gray-400"
                    }`}
                  style={watchedCity && !errors.city ? {
                    borderColor: BRAND_COLORS.primary,
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : !errors.city ? {
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : {}}
                  placeholder="Enter city"
                />
                {errors.city && (
                  <p className="flex items-center gap-1.5 text-xs text-red-600">
                    <AlertCircle className="w-3 h-3 flex-shrink-0" />
                    {errors.city.message}
                  </p>
                )}
              </div>

              {/* State */}
              <div className="space-y-2">
                <label htmlFor="state" className="block text-sm font-medium text-gray-900">
                  State <span className="text-red-500">*</span>
                </label>
                <input
                  id="state"
                  type="text"
                  {...register("state")}
                  className={`h-10 w-full rounded-lg border bg-white px-3 py-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.state
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                    : watchedState && !errors.state
                      ? `focus:ring-opacity-20`
                      : "border-gray-300 hover:border-gray-400"
                    }`}
                  style={watchedState && !errors.state ? {
                    borderColor: BRAND_COLORS.primary,
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : !errors.state ? {
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : {}}
                  placeholder="Enter state"
                />
                {errors.state && (
                  <p className="flex items-center gap-1.5 text-xs text-red-600">
                    <AlertCircle className="w-3 h-3 flex-shrink-0" />
                    {errors.state.message}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Country */}
              <div className="space-y-2">
                <label htmlFor="country" className="block text-sm font-medium text-gray-900">
                  Country <span className="text-red-500">*</span>
                </label>
                <input
                  id="country"
                  type="text"
                  {...register("country")}
                  className={`h-10 w-full rounded-lg border bg-white px-3 py-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.country
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                    : "border-gray-300 hover:border-gray-400"
                    }`}
                  placeholder="Enter country"
                />
                {errors.country && (
                  <p className="flex items-center gap-1.5 text-xs text-red-600">
                    <AlertCircle className="w-3 h-3 flex-shrink-0" />
                    {errors.country.message}
                  </p>
                )}
              </div>

              {/* Pincode */}
              <div className="space-y-2">
                <label htmlFor="pincode" className="block text-sm font-medium text-gray-900">
                  Pincode <span className="text-red-500">*</span>
                </label>
                <input
                  id="pincode"
                  type="text"
                  {...register("pincode")}
                  className={`h-10 w-full rounded-lg border bg-white px-3 py-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.pincode
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                    : watchedPincode && !errors.pincode
                      ? `focus:ring-opacity-20`
                      : "border-gray-300 hover:border-gray-400"
                    }`}
                  style={watchedPincode && !errors.pincode ? {
                    borderColor: BRAND_COLORS.primary,
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : !errors.pincode ? {
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : {}}
                  placeholder="Enter 6-digit pincode"
                />
                {errors.pincode && (
                  <p className="flex items-center gap-1.5 text-xs text-red-600">
                    <AlertCircle className="w-3 h-3 flex-shrink-0" />
                    {errors.pincode.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="px-6"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !isFormValid}
              className="px-8 min-w-[140px]"
              style={{ backgroundColor: BRAND_COLORS.primary }}
              onClick={() => {
                const values = getValues();
                console.log('Full form values:', JSON.stringify(values, null, 2));
              }}
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  <span>{isEditMode ? 'Updating...' : 'Creating...'}</span>
                </div>
              ) : (
                <span>{isEditMode ? 'Update Customer' : 'Add Customer'}</span>
              )}
            </Button>
          </div>
        </form>
      </div>
    </motion.div>
  );
}